import { type CSSProperties, useEffect, useState } from 'react';

import { clipAudio, initMouseAudio } from '../lib/mouse-audio';

/** Canned clips a mouse can play; 'scurry' also moves it along the tabletop. */
type ClipName = 'idle' | 'sniff' | 'groom' | 'sit' | 'scurry';

/** Base length of each non-moving clip; actual length gets ±25% jitter. */
const CLIP_BASE_MS: Record<Exclude<ClipName, 'scurry'>, number> = {
  idle: 2600,
  sniff: 1700,
  groom: 2300,
  sit: 2900,
};

/** Pixels of tabletop a mouse can roam (table width minus mouse and margins). */
const TABLE_RUN_PX = 114;

/** Each mouse patrols its own strip of tabletop, 0..1 of TABLE_RUN_PX. */
type MouseRange = readonly [number, number];

type MouseState = {
  clip: ClipName;
  /** Position along the tabletop, 0..1 of TABLE_RUN_PX. */
  x: number;
  facing: 1 | -1;
  /** Transition time for the current scurry; 0 for stationary clips. */
  moveMs: number;
};

type MouseStyle = CSSProperties & {
  '--mouse-x': number;
  '--mouse-facing': number;
  /** Desyncs the always-on part animations (blink, ear twitch, tail sway). */
  '--mouse-seed': number;
  /** Duration of the current scurry's slide; facing flips on its own fast clock. */
  '--mouse-move-ms': string;
};

function pickNextClip(current: ClipName): ClipName {
  const pool: ClipName[] = ['idle', 'idle', 'sniff', 'groom', 'sit', 'scurry', 'scurry'];
  const options = pool.filter((clip) => clip !== current);
  return options[Math.floor(Math.random() * options.length)];
}

function scurryFrom(current: MouseState, range: MouseRange): MouseState {
  // Always cross to the other end of the territory so runs read as deliberate.
  const [lo, hi] = range;
  const span = hi - lo;
  const x = current.x > lo + span / 2
    ? lo + Math.random() * span * 0.35
    : hi - span * 0.35 + Math.random() * span * 0.35;
  return {
    clip: 'scurry',
    x,
    facing: x > current.x ? 1 : -1,
    moveMs: Math.round(400 + Math.abs(x - current.x) * 1600),
  };
}

function clipDurationMs(state: MouseState): number {
  if (state.clip === 'scurry') return state.moveMs + 200;
  return Math.round(CLIP_BASE_MS[state.clip] * (0.75 + Math.random() * 0.5));
}

function Mouse({ initialX, initialDelayMs, range }: {
  initialX: number;
  initialDelayMs: number;
  range: MouseRange;
}) {
  const [state, setState] = useState<MouseState>({ clip: 'idle', x: initialX, facing: 1, moveMs: 0 });

  useEffect(() => {
    let timer = 0;
    const advance = (current: MouseState) => {
      const clip = pickNextClip(current.clip);
      const next: MouseState = clip === 'scurry'
        ? scurryFrom(current, range)
        : { ...current, clip, moveMs: 0 };
      clipAudio(next.clip, next.x, next.moveMs);
      setState(next);
      timer = window.setTimeout(() => advance(next), clipDurationMs(next));
    };
    timer = window.setTimeout(
      () => advance({ clip: 'idle', x: initialX, facing: 1, moveMs: 0 }),
      initialDelayMs,
    );
    return () => window.clearTimeout(timer);
  }, [initialX, initialDelayMs, range]);

  const style: MouseStyle = {
    '--mouse-x': state.x,
    '--mouse-facing': state.facing,
    '--mouse-seed': initialX,
    '--mouse-move-ms': `${state.moveMs}ms`,
  };

  return (
    <div className={`table-mouse clip-${state.clip}`} style={style}>
      <svg className="mouse-sprite" viewBox="0 0 40 24">
        <ellipse className="mouse-shadow" cx="17" cy="23" rx="12" ry="1.3" />
        <g className="mouse-root">
          <g className="mouse-tail-g">
            <path className="mouse-tail" d="M9 17.6 C 3.4 19.6, .6 15.6, 2 11.2 C 2.8 8.8, 4.8 8.2, 5.9 9.3" />
          </g>
          <circle className="mouse-haunch" cx="11.8" cy="16.4" r="5.1" />
          <ellipse className="mouse-foot-back" cx="12.6" cy="22.2" rx="3.1" ry="1.1" />
          <path className="mouse-leg mouse-leg-a" d="M21.6 18.6 L21.2 22.4" />
          <path className="mouse-leg mouse-leg-b" d="M24.4 18.4 L24.9 22.3" />
          <ellipse className="mouse-body" cx="16.5" cy="15.8" rx="9.6" ry="6" />
          <path className="mouse-sheen" d="M8 12.4 C 12.5 9.2, 20 8.3, 25.4 10.6" />
          <g className="mouse-head-g">
            <circle className="mouse-ear mouse-ear-far" cx="24.9" cy="6.6" r="2.8" />
            <path className="mouse-head" d="M22.4 9.4 C 27.2 6.4, 33.2 8.2, 36.9 12.4 C 33.4 16.7, 26.8 17.3, 22.6 15.3 Z" />
            <g className="mouse-ear-near-g">
              <circle className="mouse-ear" cx="28.4" cy="6.9" r="3.1" />
              <circle className="mouse-ear-inner" cx="28.7" cy="7.15" r="1.55" />
            </g>
            <g className="mouse-eye-g">
              <circle className="mouse-eye" cx="30.5" cy="11.4" r="1.1" />
              <circle className="mouse-eye-glint" cx="30.9" cy="11.05" r=".35" />
            </g>
            <circle className="mouse-nose" cx="37" cy="12.5" r=".85" />
            <g className="mouse-whiskers">
              <path d="M35.6 12.2 C 38 10.8, 39.6 10.2, 41.4 9.9" />
              <path d="M35.8 12.9 C 38.6 12.5, 40.4 12.4, 42 12.5" />
              <path d="M35.6 13.5 C 38 14.2, 39.6 14.8, 41.2 15.4" />
            </g>
          </g>
          <g className="mouse-paws">
            <path d="M27.4 16.2 C 28.4 15.2, 29.2 14.6, 29.8 14.4" />
            <path d="M29 16.8 C 30 15.8, 30.8 15.2, 31.4 15" />
          </g>
        </g>
      </svg>
    </div>
  );
}

/* Territories are separated by more than a sprite width so mice never overlap. */
const LEFT_RANGE: MouseRange = [0, 0.34];
const RIGHT_RANGE: MouseRange = [0.68, 1];

/** A small table against the wall, outside the frame, with two resident mice. */
export function MouseTable() {
  useEffect(() => {
    initMouseAudio();
  }, []);

  return (
    <div className="mouse-table" aria-hidden="true">
      {/* Shared gradient definitions referenced by both mouse sprites. */}
      <svg className="mouse-defs" aria-hidden="true" focusable="false">
        <defs>
          <radialGradient id="mouseBodyGrad" cx="38%" cy="24%" r="88%">
            <stop offset="0%" stopColor="#5e5142" />
            <stop offset="45%" stopColor="#443b31" />
            <stop offset="100%" stopColor="#241f19" />
          </radialGradient>
          <radialGradient id="mouseHaunchGrad" cx="35%" cy="22%" r="90%">
            <stop offset="0%" stopColor="#544838" />
            <stop offset="50%" stopColor="#3a322a" />
            <stop offset="100%" stopColor="#211c16" />
          </radialGradient>
          <radialGradient id="mouseHeadGrad" cx="42%" cy="20%" r="92%">
            <stop offset="0%" stopColor="#5f5140" />
            <stop offset="50%" stopColor="#463d32" />
            <stop offset="100%" stopColor="#262019" />
          </radialGradient>
          <radialGradient id="mouseEarGrad" cx="45%" cy="30%" r="75%">
            <stop offset="0%" stopColor="#6d5d4a" />
            <stop offset="100%" stopColor="#41372c" />
          </radialGradient>
          <radialGradient id="mouseEarInnerGrad" cx="50%" cy="45%" r="70%">
            <stop offset="0%" stopColor="#8a6349" />
            <stop offset="100%" stopColor="#54402f" />
          </radialGradient>
          <linearGradient id="mouseTailGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#332b22" />
            <stop offset="100%" stopColor="#584c3d" />
          </linearGradient>
        </defs>
      </svg>
      <div className="table-top" />
      <div className="table-apron" />
      <div className="table-leg table-leg-left" />
      <div className="table-leg table-leg-right" />
      <div className="table-shadow" />
      <Mouse initialX={0.18} initialDelayMs={1200} range={LEFT_RANGE} />
      <Mouse initialX={0.72} initialDelayMs={2800} range={RIGHT_RANGE} />
    </div>
  );
}
